import type { CccCalendarEvent } from '../../api/cccCalendarApi';
import type { CccClientType } from '../../config/cccClientRoutes';

export type CalendarView = 'day' | 'week' | 'month';

export type CccCalEvent = {
  id: number;
  starts_at: string;
  ends_at: string;
  title: string;
  subtitle?: string | null;
  tone: CccClientType;
  status: 'scheduled' | 'cancelled';
  ownerName?: string | null;
  source: CccCalendarEvent;
};

export const DAY_START_HOUR = 0;
export const DAY_END_HOUR = 24;
export const HOUR_HEIGHT = 52;
export const SLOT_MINUTES = 30;

export const PT_MONTH_LONG = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export const PT_WEEKDAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
export const PT_WEEKDAY_MIN = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

export const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const startOfWeek = (date: Date): Date => {
  const d = startOfDay(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

export const startOfMonth = (date: Date): Date => {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
};

export const endOfMonth = (date: Date): Date => {
  const d = startOfMonth(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d;
};

export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  return d;
};

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const toDateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const parseIsoLocal = (iso: string): Date => {
  const cleaned = iso.replace(/Z$/, '');
  const d = new Date(cleaned.includes('T') ? cleaned : `${cleaned}T00:00:00`);
  return d;
};

export const formatTime = (date: Date): string =>
  date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });

export const formatDateLong = (date: Date): string => {
  const day = date.getDate();
  const month = PT_MONTH_LONG[date.getMonth()];
  const year = date.getFullYear();
  return `${day} de ${month} de ${year}`;
};

export const formatMonthLabel = (date: Date): string => {
  const month = PT_MONTH_LONG[date.getMonth()];
  return `${month} ${date.getFullYear()}`;
};

export const formatWeekRangeShort = (weekStart: Date): string => {
  const end = addDays(weekStart, 6);
  const startMonth = PT_MONTH_LONG[weekStart.getMonth()];
  const endMonth = PT_MONTH_LONG[end.getMonth()];
  const startYear = weekStart.getFullYear();
  const endYear = end.getFullYear();
  const sameYear = startYear === endYear;
  const sameMonth = sameYear && weekStart.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${weekStart.getDate()} – ${end.getDate()} ${startMonth} ${startYear}`;
  }
  if (sameYear) {
    return `${weekStart.getDate()} ${startMonth} – ${end.getDate()} ${endMonth} ${startYear}`;
  }
  return `${weekStart.getDate()} ${startMonth} ${startYear} – ${end.getDate()} ${endMonth} ${endYear}`;
};

export const monthGridDays = (monthDate: Date): Date[] => {
  const first = startOfMonth(monthDate);
  const gridStart = startOfWeek(first);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
};

export type LaidOutEvent = {
  event: CccCalEvent;
  col: number;
  cols: number;
};

export const layoutEventsInDay = (events: CccCalEvent[]): LaidOutEvent[] => {
  const sorted = [...events]
    .filter((ev) => ev.status !== 'cancelled')
    .sort((a, b) => parseIsoLocal(a.starts_at).getTime() - parseIsoLocal(b.starts_at).getTime());

  const clusters: CccCalEvent[][] = [];
  let current: CccCalEvent[] = [];
  let currentEnd = 0;

  sorted.forEach((ev) => {
    const s = parseIsoLocal(ev.starts_at).getTime();
    const e = parseIsoLocal(ev.ends_at).getTime();
    if (current.length && s >= currentEnd) {
      clusters.push(current);
      current = [];
      currentEnd = 0;
    }
    current.push(ev);
    currentEnd = Math.max(currentEnd, e);
  });
  if (current.length) clusters.push(current);

  const result: LaidOutEvent[] = [];
  clusters.forEach((cluster) => {
    const columnEnds: number[] = [];
    const assignments = new Map<number, number>();
    cluster.forEach((ev) => {
      const s = parseIsoLocal(ev.starts_at).getTime();
      const e = parseIsoLocal(ev.ends_at).getTime();
      let col = columnEnds.findIndex((endTime) => endTime <= s);
      if (col === -1) {
        col = columnEnds.length;
        columnEnds.push(e);
      } else {
        columnEnds[col] = e;
      }
      assignments.set(ev.id, col);
    });
    const cols = columnEnds.length;
    cluster.forEach((ev) => {
      result.push({ event: ev, col: assignments.get(ev.id) ?? 0, cols });
    });
  });

  return result;
};

export const eventsOverlap = (
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date,
): boolean => aStart < bEnd && bStart < aEnd;

export const findConflictingEventIds = (events: CccCalEvent[]): Set<number> => {
  const active = events.filter((ev) => ev.status !== 'cancelled');
  const conflictIds = new Set<number>();
  for (let i = 0; i < active.length; i += 1) {
    const a = active[i];
    const aStart = parseIsoLocal(a.starts_at);
    const aEnd = parseIsoLocal(a.ends_at);
    for (let j = i + 1; j < active.length; j += 1) {
      const b = active[j];
      if (eventsOverlap(aStart, aEnd, parseIsoLocal(b.starts_at), parseIsoLocal(b.ends_at))) {
        conflictIds.add(a.id);
        conflictIds.add(b.id);
      }
    }
  }
  return conflictIds;
};

export const countConflictPairs = (events: CccCalEvent[]): number => {
  const active = events.filter((ev) => ev.status !== 'cancelled');
  let pairs = 0;
  for (let i = 0; i < active.length; i += 1) {
    const aStart = parseIsoLocal(active[i].starts_at);
    const aEnd = parseIsoLocal(active[i].ends_at);
    for (let j = i + 1; j < active.length; j += 1) {
      if (
        eventsOverlap(
          aStart,
          aEnd,
          parseIsoLocal(active[j].starts_at),
          parseIsoLocal(active[j].ends_at),
        )
      ) {
        pairs += 1;
      }
    }
  }
  return pairs;
};

export function toCccCalEvent(
  ev: CccCalendarEvent,
  ownerName?: string | null,
): CccCalEvent {
  return {
    id: ev.id,
    starts_at: ev.start_at,
    ends_at: ev.end_at,
    title: ev.title || 'Evento',
    subtitle: ev.client_label || ownerName || null,
    tone: ev.client_type || 'none',
    status: 'scheduled',
    ownerName: ownerName || null,
    source: ev,
  };
}
